import { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import { TChannel, TChannelResponseStatus } from '../api/messages';

type ChannelPage = { data: TChannel[] };
type ChannelPages = InfiniteData<ChannelPage>;

// Preserve page metadata and membership until the next server refresh. In particular,
// changing a row in "needs response only" must not remove it under the pointer.
export function updateChannelResponseStatusPages<T extends ChannelPage>(
  data: InfiniteData<T> | undefined,
  channelId: string,
  responseStatus: TChannel['responseStatus'],
  expectedStatus?: TChannelResponseStatus,
): InfiniteData<T> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data.map((channel) =>
        channel.kind === 'MALE' &&
        channel.id === channelId &&
        (!expectedStatus || channel.responseStatus === expectedStatus)
          ? { ...channel, responseStatus }
          : channel,
      ),
    })),
  };
}

export function patchChannelResponseStatus(
  queryClient: QueryClient,
  channelId: string,
  responseStatus: TChannelResponseStatus,
  expectedStatus?: TChannelResponseStatus,
) {
  queryClient.setQueriesData<ChannelPages>(
    { queryKey: ['management-channels'] },
    (data) =>
      updateChannelResponseStatusPages(
        data,
        channelId,
        responseStatus,
        expectedStatus,
      ),
  );
  queryClient.setQueryData<TChannel>(
    ['channel-detail', 'male', channelId],
    (channel) =>
      channel && (!expectedStatus || channel.responseStatus === expectedStatus)
        ? { ...channel, responseStatus }
        : channel,
  );
}

type StatusSnapshot = {
  lists: Array<[QueryKey, TChannel['responseStatus']]>;
  detail: TChannel['responseStatus'];
};

export function snapshotChannelResponseStatus(
  queryClient: QueryClient,
  channelId: string,
): StatusSnapshot {
  const lists: StatusSnapshot['lists'] = [];
  for (const [key, data] of queryClient.getQueriesData<ChannelPages>({
    queryKey: ['management-channels'],
  })) {
    const channel = data?.pages
      .flatMap((page) => page.data)
      .find((row) => row.id === channelId);
    if (channel) lists.push([key, channel.responseStatus]);
  }
  return {
    lists,
    detail: queryClient.getQueryData<TChannel>([
      'channel-detail',
      'male',
      channelId,
    ])?.responseStatus,
  };
}

export function restoreChannelResponseStatus(
  queryClient: QueryClient,
  channelId: string,
  attemptedStatus: TChannelResponseStatus,
  snapshot: StatusSnapshot,
) {
  // Restore only this field; preserve new messages, unread counts, other rows and
  // a newer incoming status instead of replacing entire cached pages.
  for (const [key, responseStatus] of snapshot.lists) {
    queryClient.setQueryData<ChannelPages>(key, (data) =>
      updateChannelResponseStatusPages(
        data,
        channelId,
        responseStatus,
        attemptedStatus,
      ),
    );
  }
  queryClient.setQueryData<TChannel>(
    ['channel-detail', 'male', channelId],
    (channel) =>
      channel?.responseStatus === attemptedStatus
        ? { ...channel, responseStatus: snapshot.detail }
        : channel,
  );
}
